
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, Camera, Zap, Sparkles, Loader, User as UserIcon, Bot, ImageIcon, Plus, Mic, X, Dumbbell, Utensils } from 'lucide-react';
import { collection, addDoc, onSnapshot, query, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import { db, auth } from './firebaseConfig';
import { User, Message, SupportedLanguage } from './types';
import * as GeminiService from './services/geminiService';
import { saveAIPlan } from './services/planService';
import { checkAndSendProactiveMessages } from './services/proactiveAIService';

export const AICoachView = ({ user, authReady, lang }: { user: User, authReady: boolean, lang: SupportedLanguage }) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // 🔑 CRITICAL: Block ALL snapshots until auth is ready
    if (!authReady) return;
    
    // 🔒 CRITICAL FIX: Verify user.id === auth.currentUser.uid BEFORE creating listener
    const authUid = auth.currentUser?.uid;
    if (!authUid || authUid !== user.id) {
      if (import.meta.env.DEV) {
        console.warn('[AI_COACH] onSnapshot blocked: user.id !== auth.currentUser.uid');
      }
      setMessages([]);
      return;
    }

    const q = query(
      collection(db, "users", user.id, "coachMessages"),
      orderBy("timestamp", "asc"),
      limit(50)
    );
    
    // CRITICAL: Initialize as null, assign only if onSnapshot returns a function
    let unsubscribe: (() => void) | null = null;
    
    try {
      const snapshotResult = onSnapshot(q, (snap) => {
        setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() } as Message)));
      });
      if (typeof snapshotResult === 'function') {
        unsubscribe = snapshotResult;
      }
    } catch (initError: any) {
      if (import.meta.env.DEV) {
        console.error('[AI_COACH] Error initializing snapshot listener:', initError);
      }
    }
    
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [authReady, user.id]);

  // Proactive AI: Check and send proactive messages when view loads
  useEffect(() => {
    // Only if no messages exist (first time)
    if (messages.length === 0) {
      checkAndSendProactiveMessages(user, lang).catch(console.error);
    }
  }, [authReady, user.id]); // Only run once when user changes

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleGeneratePlan = async (type: 'workout' | 'diet') => {
    setLoading(true);
    const prompt = type === 'workout' 
      ? t('coach.generate_workout')
      : t('coach.generate_diet');
    
    try {
      // Aggiungi messaggio utente
      await addDoc(collection(db, "users", user.id, "coachMessages"), {
        role: 'user',
        text: prompt,
        timestamp: serverTimestamp()
      });

      let planData;
      if (type === 'workout') {
        planData = await GeminiService.generateWorkoutPlan("General Fitness", "Bodyweight/Gym", user, lang);
        await saveAIPlan(user.id, 'workout', planData.title || t('coach.new_workout'), planData);
      } else {
        planData = await GeminiService.generateDietPlan("Healthy Living", "Balanced", user, lang);
        await saveAIPlan(user.id, 'diet', planData.title || t('coach.new_diet'), planData);
      }

      // Aggiungi risposta coach
      await addDoc(collection(db, "users", user.id, "coachMessages"), {
        role: 'model',
        text: type === 'workout' ? t('coach.plan_generated_workout') : t('coach.plan_generated_diet'),
        timestamp: serverTimestamp()
      });
    } catch (err) {
      console.error(err);
      alert(t('coach.generation_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!input.trim() && !previewImage) return;

    const userMessage = input;
    setInput('');
    setPreviewImage(null);
    setLoading(true);

    try {
      await addDoc(collection(db, "users", user.id, "coachMessages"), {
        role: 'user',
        text: userMessage,
        timestamp: serverTimestamp()
      });

      const response = await GeminiService.chatWithCoach(userMessage, lang, user, messages);

      await addDoc(collection(db, "users", user.id, "coachMessages"), {
        role: 'model',
        text: response.text,
        groundingChunks: response.groundingChunks || [],
        timestamp: serverTimestamp()
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreviewImage(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] md:h-screen bg-slate-950">
      {/* Header */}
      <div className="p-4 border-b border-slate-900 bg-slate-950/50 backdrop-blur-md flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-500 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
            <Zap className="text-white" size={20} fill="currentColor" />
          </div>
          <div>
            <h2 className="font-black text-sm uppercase tracking-widest">{t('nav.coach')}</h2>
            <p className="text-[10px] text-emerald-500 font-bold uppercase tracking-tighter">{t('coach.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
           <span className="flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
           <span className="text-[10px] font-bold text-slate-500">SYSTEM ACTIVE</span>
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center space-y-6 opacity-50">
             <div className="p-6 bg-slate-900 rounded-[2.5rem] border border-slate-800">
                <Sparkles size={48} className="text-emerald-500 mb-4 mx-auto" />
                <h3 className="font-black text-xl mb-2 text-white uppercase italic">{t('coach.welcome_title')}</h3>
                <p className="max-w-xs text-xs leading-relaxed">
                  {t('coach.intro')}
                </p>
             </div>
             
             <div className="grid grid-cols-2 gap-3 max-w-sm">
                <button onClick={() => handleGeneratePlan('workout')} className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl flex flex-col items-center gap-2 hover:bg-emerald-500/20 transition-all">
                  <Dumbbell className="text-emerald-500" size={24}/>
                  <span className="text-[10px] font-black uppercase text-white tracking-widest">{t('coach.generate_workout_button')}</span>
                </button>
                <button onClick={() => handleGeneratePlan('diet')} className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-2xl flex flex-col items-center gap-2 hover:bg-orange-500/20 transition-all">
                  <Utensils className="text-orange-500" size={24}/>
                  <span className="text-[10px] font-black uppercase text-white tracking-widest">{t('coach.generate_diet_button')}</span>
                </button>
             </div>
          </div>
        )}

        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-[1.5rem] p-4 ${
              msg.role === 'user' 
                ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/10' 
                : 'bg-slate-900 border border-slate-800 text-slate-100'
            }`}>
              <div className="flex items-center gap-2 mb-2 opacity-50">
                {msg.role === 'user' ? <UserIcon size={12}/> : <Bot size={12}/>}
                <span className="text-[9px] font-black uppercase tracking-widest">
                  {msg.role === 'user' ? user.name : t('coach.name')}
                </span>
              </div>
              <p className="text-sm leading-relaxed whitespace-pre-wrap font-medium">{msg.text}</p>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
             <div className="bg-slate-900 border border-slate-800 p-4 rounded-2xl flex items-center gap-3">
                <Loader className="animate-spin text-emerald-500" size={18} />
                <span className="text-xs font-bold text-slate-500 italic">{t('coach.thinking')}</span>
             </div>
          </div>
        )}
        <div ref={scrollRef} />
      </div>

      {/* Input Area */}
      <div className="p-4 bg-slate-950 border-t border-slate-900">
        <form onSubmit={handleSend} className="flex gap-3">
          <div className="flex-1 relative flex items-center">
             <input 
               type="text" 
               value={input}
               onChange={e => setInput(e.target.value)}
               placeholder={t('coach.input_placeholder')}
               className="w-full bg-slate-900 border border-slate-800 rounded-2xl p-4 pr-24 outline-none focus:border-emerald-500 transition-all font-medium text-sm"
             />
             <div className="absolute right-2 flex items-center gap-1">
                <label className="p-2 text-slate-500 hover:text-emerald-500 cursor-pointer transition-colors">
                   <Camera size={20}/>
                   <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                </label>
                <button 
                  type="button" 
                  onClick={() => {
                    alert(t('coach.voice_coming_soon') || t('common.coming_soon'));
                  }}
                  className="p-2 text-slate-500 hover:text-emerald-500 transition-colors"
                  title={t('coach.voice_input')}
                >
                   <Mic size={20}/>
                </button>
             </div>
          </div>
          <button 
            type="submit" 
            disabled={loading || (!input.trim() && !previewImage)}
            className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white p-4 rounded-2xl shadow-lg shadow-emerald-500/20 transition-all"
          >
            <Send size={20} />
          </button>
        </form>
      </div>
    </div>
  );
};
